import { memo } from "react";
import { Button } from "@/shared/ui/button";
import { Tooltip } from "@/shared/ui/tooltip";
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Download,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";

const CONFIDENCE_FILTER_VALUES = {
  ALL: "all",
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;

function getConfidenceFilterValue(
  confidenceFilter: number | null
): (typeof CONFIDENCE_FILTER_VALUES)[keyof typeof CONFIDENCE_FILTER_VALUES] {
  if (confidenceFilter === null) {
    return CONFIDENCE_FILTER_VALUES.ALL;
  }
  if (confidenceFilter === 0.9) {
    return CONFIDENCE_FILTER_VALUES.HIGH;
  }
  if (confidenceFilter === 0.5) {
    return CONFIDENCE_FILTER_VALUES.MEDIUM;
  }
  return CONFIDENCE_FILTER_VALUES.LOW;
}

function getConfidenceFilterFromValue(value: string): number | null {
  if (value === CONFIDENCE_FILTER_VALUES.ALL) {
    return null;
  }
  if (value === CONFIDENCE_FILTER_VALUES.HIGH) {
    return 0.9;
  }
  if (value === CONFIDENCE_FILTER_VALUES.MEDIUM) {
    return 0.5;
  }
  if (value === CONFIDENCE_FILTER_VALUES.LOW) {
    return -1;
  }
  return null;
}

interface ImageDialogControlsProps {
  zoom: number;
  isFullscreen: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFullscreen: () => void;
  onDownload: () => void;
  isDownloading: boolean;
  hasClassifications?: boolean;
  confidenceFilter: number | null;
  onConfidenceFilterChange: (filter: number | null) => void;
}

function ImageDialogControlsComponent({
  zoom,
  isFullscreen,
  onZoomIn,
  onZoomOut,
  onFullscreen,
  onDownload,
  isDownloading,
  hasClassifications,
  confidenceFilter,
  onConfidenceFilterChange,
}: ImageDialogControlsProps) {
  const getFilterLabel = () => {
    if (confidenceFilter === null) return "All";
    if (confidenceFilter === 0.9) return "High (≥90%)";
    if (confidenceFilter === 0.5) return "Medium (50-90%)";
    if (confidenceFilter === -1) return "Low (<50%)";
    return "All";
  };

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
      <Button
        variant="ghost"
        size="icon"
        className="text-white hover:bg-white/20"
        onClick={onZoomOut}
        disabled={zoom <= 0.1}
        aria-label="Zoom out"
      >
        <ZoomOut className="h-4 w-4" />
      </Button>

      <span className="text-white text-sm min-w-[60px] text-center">
        {Math.round(zoom * 100)}%
      </span>

      <Button
        variant="ghost"
        size="icon"
        className="text-white hover:bg-white/20"
        onClick={onZoomIn}
        aria-label="Zoom in"
      >
        <ZoomIn className="h-4 w-4" />
      </Button>

      <div className="w-px h-6 bg-white/20 mx-2" />

      <Button
        variant="ghost"
        size="icon"
        className="text-white hover:bg-white/20"
        onClick={onFullscreen}
        aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      >
        {isFullscreen ? (
          <Minimize2 className="h-4 w-4" />
        ) : (
          <Maximize2 className="h-4 w-4" />
        )}
      </Button>

      <div className="w-px h-6 bg-white/20 mx-2" />

      <Tooltip content="Download file">
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/20"
          aria-label="Download file"
          onClick={onDownload}
          disabled={isDownloading}
        >
          <Download className="h-4 w-4" />
        </Button>
      </Tooltip>

      {hasClassifications && (
        <>
          <div className="w-px h-6 bg-white/20 mx-2" />
          <DropdownMenu>
            <Tooltip content="Filter bounding boxes by confidence">
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/20 text-sm gap-2"
                >
                  <span>Confidence: {getFilterLabel()}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Confidence Filter</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={getConfidenceFilterValue(confidenceFilter)}
                onValueChange={(value: string) => {
                  onConfidenceFilterChange(getConfidenceFilterFromValue(value));
                }}
              >
                <DropdownMenuRadioItem value={CONFIDENCE_FILTER_VALUES.ALL}>
                  All
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={CONFIDENCE_FILTER_VALUES.HIGH}>
                  High (≥90%)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={CONFIDENCE_FILTER_VALUES.MEDIUM}>
                  Medium (50-90%)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value={CONFIDENCE_FILTER_VALUES.LOW}>
                  Low (&lt;50%)
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}

export const ImageDialogControls = memo(ImageDialogControlsComponent);
